/**
 * Utility functions for filename validation
 * Prevents special characters that can cause issues with database storage and PDF generation
 */

/**
 * Checks if a filename contains special characters that can cause issues
 * @param filename - The filename to validate
 * @returns true if filename contains problematic characters, false otherwise
 */
export const hasSpecialCharacters = (filename: string): boolean => {
  // Check for German umlauts and other special characters that can cause issues
  const specialCharRegex = /[äöüÄÖÜßáàéèíìóòúùñçÇÁÀÉÈÍÌÓÒÚÙÑ]/;
  return specialCharRegex.test(filename);
};

/**
 * Checks if a filename contains any non-ASCII characters
 * @param filename - The filename to validate
 * @returns true if filename contains non-ASCII characters, false otherwise
 */
export const hasNonAsciiCharacters = (filename: string): boolean => {
  // Check for any non-ASCII characters by looking for characters outside printable ASCII range
  for (let i = 0; i < filename.length; i += 1) {
    const charCode = filename.charCodeAt(i);
    if (charCode < 32 || charCode > 126) {
      return true;
    }
  }
  return false;
};

/**
 * Checks if a filename contains any characters that might cause issues in URLs or file systems
 * @param filename - The filename to validate
 * @returns true if filename contains problematic characters, false otherwise
 */
export const hasProblematicCharacters = (filename: string): boolean => {
  // Check for characters that can cause issues in URLs, file systems, and databases
  const problematicChars = ['<', '>', ':', '"', '/', '\\', '|', '?', '*'];
  for (let i = 0; i < filename.length; i += 1) {
    const char = filename[i];
    if (problematicChars.includes(char)) {
      return true;
    }
    const charCode = filename.charCodeAt(i);
    if ((charCode >= 0 && charCode <= 31) || (charCode >= 127 && charCode <= 159)) {
      return true;
    }
  }
  return false;
};

/**
 * Comprehensive filename validation
 * @param filename - The filename to validate
 * @returns object with validation results
 */
export const validateFilename = (filename: string) => {
  const hasSpecial = hasSpecialCharacters(filename);
  const hasNonAscii = hasNonAsciiCharacters(filename);
  const hasProblematic = hasProblematicCharacters(filename);
  
  return {
    isValid: !hasSpecial && !hasNonAscii && !hasProblematic,
    hasSpecialCharacters: hasSpecial,
    hasNonAsciiCharacters: hasNonAscii,
    hasProblematicCharacters: hasProblematic,
    errorTypes: [
      hasSpecial && 'special',
      hasNonAscii && 'nonAscii',
      hasProblematic && 'problematic'
    ].filter(Boolean)
  };
};

/**
 * Generates a safe filename by replacing problematic characters
 * @param filename - The original filename
 * @returns A safe filename with problematic characters replaced
 */
export const sanitizeFilename = (filename: string): string => filename
  .replace(/[äöüÄÖÜß]/g, (match) => {
    const replacements: { [key: string]: string } = {
      'ä': 'ae', 'ö': 'oe', 'ü': 'ue',
      'Ä': 'Ae', 'Ö': 'Oe', 'Ü': 'Ue',
      'ß': 'ss'
    };
    return replacements[match] || match;
  })
  .replace(/[^a-zA-Z0-9._-]/g, '_')
  .replace(/_+/g, '_')
  .replace(/^_|_$/g, '');
